import { MapPin, Phone, Clock, ExternalLink } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { CallButton, WhatsAppButton } from "@/components/CTAs";
import BookingForm from "@/components/BookingForm";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CONTACT, FAQS } from "@/data/site";

export default function Contact() {
  return (
    <main data-testid="contact-page" className="pb-16 md:pb-0">
      <Seo
        title="Contact & Directions | Belong Saloon & Spa, Suchitra Hyderabad"
        description="Find Belong Saloon & Spa on Quthbullapur Main Rd, Suchitra, Hyderabad 500055 — opp. Barath Petrol Pump. Call or WhatsApp 098666 34473 to book."
      />

      <section className="mx-auto max-w-7xl px-5 pb-16 pt-40 sm:px-8">
        <Reveal>
          <Eyebrow className="mb-4">Contact Us</Eyebrow>
          <h1 className="max-w-3xl font-serif-display text-4xl leading-tight text-cream sm:text-6xl">
            We are one call <span className="italic text-gold-gradient">away</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-stone-400">
            Book in under a minute with the form below, or reach us directly — no waiting for a callback.
          </p>
        </Reveal>
      </section>

      <section id="booking" data-testid="booking-section" className="mx-auto max-w-7xl px-5 pb-20 sm:px-8">
        <Reveal>
          <div className="gold-ring grid gap-10 rounded-3xl bg-coal p-8 sm:p-12 lg:grid-cols-[1fr_1.15fr]">
            <div>
              <Eyebrow className="mb-4">Instant Booking</Eyebrow>
              <h2 className="font-serif-display text-3xl leading-tight text-cream sm:text-4xl">
                Reserve your slot in <span className="italic text-gold-gradient">under a minute</span>
              </h2>
              <p className="mt-5 text-base leading-relaxed text-stone-400">
                Pick your service, duration and a preferred date. Your request opens in WhatsApp with
                everything pre-filled — just press send, and we confirm your slot within minutes.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <CallButton testid="booking-call-button" label="Prefer to call?" />
              </div>
            </div>
            <BookingForm />
          </div>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="space-y-6">
            <Reveal>
              <div data-testid="contact-info-card" className="gold-ring rounded-2xl bg-coal p-8">
                <h2 className="font-serif-display text-2xl text-cream">Visit the sanctuary</h2>
                <ul className="mt-6 space-y-5 text-sm text-stone-300">
                  <li className="flex gap-3">
                    <MapPin size={18} className="mt-0.5 shrink-0 text-gold" />
                    <div>
                      <p data-testid="contact-address" className="leading-relaxed">{CONTACT.address}</p>
                      <a
                        href={CONTACT.mapLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-directions-link"
                        className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold transition-colors hover:text-gold-bright"
                      >
                        Get directions <ExternalLink size={12} />
                      </a>
                    </div>
                  </li>
                  <li className="flex gap-3">
                    <Phone size={18} className="shrink-0 text-gold" />
                    <a href={CONTACT.phoneTel} data-testid="contact-phone" className="transition-colors hover:text-gold">
                      {CONTACT.phoneDisplay}
                    </a>
                  </li>
                  <li className="flex gap-3">
                    <Clock size={18} className="shrink-0 text-gold" />
                    <span>{CONTACT.hours}</span>
                  </li>
                </ul>
                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <CallButton testid="contact-call-button" />
                  <WhatsAppButton testid="contact-whatsapp-button" />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="gold-ring rounded-2xl bg-ember p-8">
                <h2 className="flex items-center gap-2 font-serif-display text-2xl text-cream">
                  <span className="text-gold"><WhatsAppIcon size={20} /></span> Booking guidelines
                </h2>
                <ul className="mt-5 space-y-3 text-sm leading-relaxed text-stone-400">
                  <li>· Use the booking form above, call, or WhatsApp — whichever is easiest.</li>
                  <li>· Arrive 10 minutes early to settle in with herbal tea.</li>
                  <li>· Four Hand Massage and Jacuzzi Spa sessions start at 60 minutes.</li>
                  <li>· Same-day slots are usually available — evenings fill first.</li>
                </ul>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div data-testid="contact-map" className="gold-ring h-full min-h-[420px] overflow-hidden rounded-2xl">
              <iframe
                title="Belong Saloon and Spa location — Quthbullapur Main Rd, Suchitra, Hyderabad"
                src={CONTACT.mapEmbed}
                className="h-full min-h-[420px] w-full grayscale-[35%] invert-[92%] hue-rotate-180"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-gold/10 bg-ember/50 py-24 sm:py-28">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal className="mb-12 text-center">
            <Eyebrow className="mb-4">Good to Know</Eyebrow>
            <h2 className="font-serif-display text-3xl text-cream sm:text-4xl">Frequently asked questions</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible data-testid="faq-accordion" className="grid gap-4 md:grid-cols-2">
              {FAQS.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  data-testid={`faq-item-${i}`}
                  className="gold-ring rounded-2xl bg-coal px-6 data-[state=open]:border-gold/40"
                >
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="py-5 text-left font-serif-display text-lg text-cream hover:text-gold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`faq-content-${i}`} className="pb-5 text-sm leading-relaxed text-stone-400">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
